import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'

const languages = [
  { code: 'en', label: 'English' },
  { code: 'he', label: 'עברית' },
]

interface LanguageSwitcherProps {
  /** Tailwind/custom classes for the trigger button */
  className?: string
  /** Icon size */
  iconSize?: number
}

export default function LanguageSwitcher({ className, iconSize = 18 }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape, arrow key navigation
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
        if (!items?.length) return
        const currentIdx = Array.from(items).findIndex((el) => el === document.activeElement)
        const nextIdx = e.key === 'ArrowDown'
          ? (currentIdx + 1) % items.length
          : (currentIdx - 1 + items.length) % items.length
        items[nextIdx].focus()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  // Focus first item when opening
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
      })
    }
  }, [open])

  const switchTo = (langCode: string) => {
    if (langCode === i18n.language) {
      setOpen(false)
      return
    }

    const currentPath = location.pathname
    let newPath: string
    const langPrefixMatch = currentPath.match(/^\/(en|he)(\/.*)?$/)
    if (langPrefixMatch) {
      const rest = langPrefixMatch[2] || ''
      newPath = `/${langCode}${rest}`
    } else {
      newPath = `/${langCode}`
    }

    i18n.changeLanguage(langCode)
    navigate(newPath + location.search + location.hash, { replace: true })
    setOpen(false)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        className={className || 'btn btn-outline btn-icon'}
        aria-label={t('common.switchLanguage', { lang: '' })}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Globe size={iconSize} aria-hidden="true" />
      </button>

      {open && (
        <ul
          ref={menuRef}
          role="menu"
          aria-label={t('common.switchLanguage', { lang: '' })}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.375rem',
            minWidth: '140px',
            background: 'var(--bg, #fff)',
            border: '1px solid var(--gray-200, #e5e7eb)',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            padding: '0.25rem 0',
            zIndex: 100,
            listStyle: 'none',
          }}
        >
          {languages.map((lang) => (
            <li key={lang.code} role="none">
              <button
                role="menuitem"
                onClick={() => switchTo(lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '0.5rem 1rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: lang.code === i18n.language ? 'var(--primary)' : 'inherit',
                  fontWeight: lang.code === i18n.language ? 600 : 400,
                }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.background = 'var(--gray-100, #f3f4f6)' }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'none' }}
              >
                <span>{lang.label}</span>
                {lang.code === i18n.language && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
